import { useState, useCallback } from "react";
import { validateEmail, validateValueLenthe } from "../utils/validators";
import { validatePasswordStrength } from "../utils/password";

/**
 * Hook personnalisé pour la validation en temps réel des champs de formulaire
 * @param {string} mode - Mode du formulaire ("signin" ou "signup")
 * @returns {Object} - Objet contenant les erreurs et les fonctions de validation
 */
export const useRealtimeValidation = (mode = "signup") => {
  const [fieldErrors, setFieldErrors] = useState({});

  /**
   * Valide un champ spécifique
   * @param {string} fieldName - Nom du champ à valider
   * @param {string} value - Valeur du champ
   * @param {Object} allData - Toutes les données du formulaire (pour les comparaisons)
   * @returns {Object} - { isValid, errorMessage }
   */
  const validateField = useCallback((fieldName, value, allData = {}) => {
    // Ne pas valider les champs vides (validation légère)
    if (!value && fieldName !== "rememberMe") {
      return { isValid: true, errorMessage: "" };
    }

    switch (fieldName) {
      case "last_name":
      case "first_name":
        if (!validateValueLenthe(value, 3)) {
          return {
            isValid: false,
            errorMessage: `${fieldName === "last_name" ? "Le nom" : "Le prénom"} doit contenir au moins 3 caractères`
          };
        }
        break;

      case "email":
        if (!validateEmail(value)) {
          return {
            isValid: false,
            errorMessage: "Email invalide"
          };
        }
        break;

      case "password":
        if (mode === "signup") {
          const passwordCheck = validatePasswordStrength(value);
          if (!passwordCheck.valid) {
            return {
              isValid: false,
              errorMessage: passwordCheck.msg
            };
          }
        } else {
          // Pour signin, validation légère
          if (value.length < 6) {
            return {
              isValid: false,
              errorMessage: "Mot de passe trop court"
            };
          }
        }
        break;

      case "confpassword":
        if (mode === "signup" && allData.password && value !== allData.password) {
          return {
            isValid: false,
            errorMessage: "Les mots de passe ne correspondent pas"
          };
        }
        break;

      case "newPassword":
        const passwordCheck = validatePasswordStrength(value);
        if (!passwordCheck.valid) {
          return {
            isValid: false,
            errorMessage: passwordCheck.msg
          };
        }
        break;

      case "confirmPassword":
        if (allData.newPassword && value !== allData.newPassword) {
          return {
            isValid: false,
            errorMessage: "Les mots de passe ne correspondent pas"
          };
        }
        break;

      default:
        break;
    }

    return { isValid: true, errorMessage: "" };
  }, [mode]);

  /**
   * Met à jour l'erreur d'un champ spécifique
   */
  const updateFieldError = useCallback((fieldName, value, allData = {}) => {
    const validation = validateField(fieldName, value, allData);
    
    setFieldErrors(prev => ({
      ...prev,
      [fieldName]: validation.isValid ? "" : validation.errorMessage
    }));

    return validation.isValid;
  }, [validateField]);

  /**
   * Efface l'erreur d'un champ spécifique
   */
  const clearFieldError = useCallback((fieldName) => {
    setFieldErrors(prev => ({
      ...prev,
      [fieldName]: ""
    }));
  }, []);

  /**
   * Efface toutes les erreurs
   */
  const clearAllErrors = useCallback(() => {
    setFieldErrors({});
  }, []);

  return {
    fieldErrors,
    updateFieldError,
    clearFieldError,
    clearAllErrors,
    validateField
  };
};
